/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useEffect, useState } from 'react';

import Imgix from 'js/components/Imgix';
import { useRetracked } from 'js/lib/retracked';
import useRouter from 'js/lib/useRouter';

import type { Theme } from '@coursera/cds-core';
import { Button, Grid, Typography, Typography2, breakpoints, typography2 } from '@coursera/cds-core';
import { YellowStarIcon } from '@coursera/cds-icons';

import VideoThumbnailLink from 'bundles/clips/components/VideoThumbnailLink';
import type { ProgramSuggestion } from 'bundles/clips/components/short-form-content/SFCVLPSharedTypes';
import { END_OF_VIDEO_LINK_TRACKING_NAME } from 'bundles/clips/components/short-form-content/constants';
import type { Partner, Rating, VLPItem } from 'bundles/clips/types/vlpSharedTypes';
import { getProgramHomeMiniXdpLink } from 'bundles/clips/utils/clipsUtils';
import getDurationFormatted from 'bundles/enterprise-legacy-learner-home/utils/getDurationFormatted';
import TrackedDiv from 'bundles/page/components/TrackedDiv';
import { TrackedA } from 'bundles/page/components/TrackedLink2';
import { getNumberOfReviews } from 'bundles/product-card/utils/productCardUtils';

import _t from 'i18n!nls/clips';

type PropsFromCaller = {
  courseId?: string;
  courseName?: string;
  courseSlug: string;
  lectureItems: VLPItem[];
  itemId: string;
  isAutoPlay?: boolean;
  partner?: Partner | null;
  ratings?: Rating | null;
  skills?: Array<string>;
  accessProvidedViaProgram?: ProgramSuggestion;
};

type Props = PropsFromCaller;

const styles = {
  videoEndOverlay: () => css`
    height: 100%;
    width: 100%;
    position: absolute;
    top: 0;
    left: 0;
    padding: var(--cds-spacing-600);
    background: linear-gradient(253.44deg, rgb(0 0 0 / 0%) -61.96%, rgb(0 0 0 / 90%) 72.86%);
    ${breakpoints.down('xs')} {
      padding: var(--cds-spacing-150) var(--cds-spacing-200);
    }
  `,
  header: () => css`
    padding-bottom: var(--cds-spacing-400);
    ${breakpoints.down('xs')} {
      padding-bottom: var(--cds-spacing-150);
    }
  `,
  partnerLogo: () => css`
    background-color: white;
    height: 26px;
    width: 26px;
    border-radius: 4px;
    align-items: center;
    justify-content: center;
    display: flex;
    margin-right: var(--cds-spacing-50);
    ${breakpoints.down('xs')} {
      display: none;
    }
  `,
  partnerLogoMobile: () => css`
    background-color: white;
    height: 18px;
    width: 18px;
    border-radius: 4px;
    align-items: center;
    justify-content: center;
    display: flex;
    margin-right: var(--cds-spacing-50);
    ${breakpoints.up('sm')} {
      display: none;
    }
  `,
  partnerName: () => css`
    ${breakpoints.down('xs')} {
      ${typography2.bodySecondary}
    }
  `,
  nextVideoName: (theme: Theme) => css`
    ${theme.typography.h1}
    padding: var(--cds-spacing-150) 0;
    ${breakpoints.down('xs')} {
      padding: var(--cds-spacing-50) 0;
      ${typography2.subtitleMedium}
    }
  `,
  videoDuration: () => css`
    ${breakpoints.down('xs')} {
      display: none;
    }
  `,
  videoThumbnail: () => css`
    ${breakpoints.down('xs')} {
      display: none;
    }
  `,
  videoThumbnailMobile: () => css`
    padding-top: 0;
    ${breakpoints.up('sm')} {
      display: none;
    }
  `,
  playNowButton: () => css`
    padding: 0 var(--cds-spacing-150);
    ${typography2.subtitleMedium}
    ${breakpoints.down('xs')} {
      padding: var(--cds-spacing-100) var(--cds-spacing-150);
      box-shadow: inset 0 0 0 1px var(--cds-color-white-0);

      ${typography2.subtitleMedium}
      &:hover {
        box-shadow: inset 0 0 0 1px var(--cds-color-white-0);
      }
    }
  `,
  skills: (theme: Theme) => css`
    ${theme.typography.h2}
    ${breakpoints.down('xs')} {
      ${typography2.bodySecondary}
    }
  `,
  ratings: () => css`
    display: flex;
    margin: var(--cds-spacing-300) 0;
    ${breakpoints.down('xs')} {
      display: none;
    }
  `,
  courseMeta: () => css`
    ${breakpoints.down('xs')} {
      display: none;
    }
  `,
  reviewItem: () => css`
    margin: 0 var(--cds-spacing-50);
    align-self: center;
  `,
  seeDetailsButton: () => css`
    margin-top: var(--cds-spacing-600);

    &:hover {
      text-decoration: none;
    }
    ${breakpoints.down('xs')} {
      margin-top: var(--cds-spacing-100);
    }
  `,
};

const PartnerInfo = (partner: Partner) => {
  if (!partner || !partner.squareLogo) {
    return null;
  }
  const { id, name, squareLogo } = partner;
  return (
    <div css={{ display: 'flex' }}>
      <div css={styles.partnerLogo}>
        <Imgix src={squareLogo} key={id} alt={name} maxHeight={22} maxWidth={22} height={22} width={22} />
      </div>
      <div css={styles.partnerLogoMobile}>
        <Imgix src={squareLogo} key={id} alt={name} maxHeight={14} maxWidth={14} height={14} width={14} />
      </div>
      <Typography2 component="p" variant="bodyPrimary" color="invertBody" css={styles.partnerName}>
        {name}
      </Typography2>
    </div>
  );
};

const CourseRatings = (rating: Rating) => {
  if (!rating || !rating.averageFiveStarRating || !rating.ratingCount) {
    return null;
  }
  const { averageFiveStarRating, ratingCount } = rating;
  const averageRating = averageFiveStarRating.toFixed(1);
  const numOfReviews = getNumberOfReviews(ratingCount);

  return (
    <div css={styles.ratings}>
      <Typography2
        variant="subtitleMedium"
        component="p"
        color="invertBody"
        aria-label={_t('#{rating} out of 5 stars', { rating: averageRating })}
      >
        {averageRating}
      </Typography2>
      <YellowStarIcon size="small" css={styles.reviewItem} aria-hidden="true" />
      <Typography2 component="p" variant="bodySecondary" color="invertBody">
        {_t('(#{numOfReviews} reviews)', {
          numOfReviews,
        })}
      </Typography2>
    </div>
  );
};

function useCountdownTime({
  isAutoPlay,
  courseSlug,
  lectureItems,
  itemId,
}: {
  isAutoPlay: boolean;
  courseSlug: string;
  lectureItems: VLPItem[];
  itemId: string;
}) {
  const [countdownTime, setCountdownTime] = useState(9);
  const [nextVideoInfo, setNextVideoInfo] = useState<{
    id: string;
    name: string;
    timeCommitment: number;
    pathname: string;
    query: Record<string, string>;
  } | null>(null);
  const router = useRouter();
  const {
    location: { query },
  } = router;
  useEffect(
    () => {
      const indexInCourse = lectureItems.findIndex((item) => item.id === itemId);
      if (indexInCourse > -1 && indexInCourse + 1 < lectureItems.length) {
        const { id, name, timeCommitment } = lectureItems[indexInCourse + 1];
        setNextVideoInfo({
          id,
          name,
          timeCommitment,
          pathname: `/videos/${courseSlug}/${id}`,
          query,
        });
      }
    }, // FIXME: existing react-hooks/exhaustive-deps violations are excused to prevent seeing errors when modifying other parts of the same file; please fix it carefully
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [itemId, lectureItems, courseSlug]
  );

  useEffect(() => {
    let intervalId: number;
    if (nextVideoInfo && isAutoPlay) {
      intervalId = window.setInterval(() => {
        setCountdownTime((prevTime) => prevTime - 1);
      }, 1000);
    }
    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [nextVideoInfo, isAutoPlay]);

  useEffect(() => {
    if (countdownTime === 0 && nextVideoInfo) {
      router.push({ pathname: nextVideoInfo.pathname, query: nextVideoInfo.query });
    }
  }, [countdownTime, nextVideoInfo, router]);
  return { nextVideoInfo, countdownTime };
}

export const VideoEndOverlay: React.FC<Props> = ({
  courseId,
  courseName,
  courseSlug,
  lectureItems,
  itemId,
  isAutoPlay = false,
  partner,
  ratings,
  skills,
  accessProvidedViaProgram,
}) => {
  const router = useRouter();
  const {
    location: {
      query: { org: orgSlug, program: programSlug },
    },
  } = router;

  const trackEvent = useRetracked();
  const { nextVideoInfo, countdownTime } = useCountdownTime({ isAutoPlay, courseSlug, lectureItems, itemId });

  const totalCourseDuration = getDurationFormatted(
    lectureItems.reduce((acc, lectureItem) => acc + lectureItem.timeCommitment, 0)
  );

  const programHomeMiniXdpLink = getProgramHomeMiniXdpLink({
    courseId,
    orgSlug,
    programSlug,
    accessProvidedViaProgram,
    trackingName: 'sfc_end_card_course_link',
    itemId,
  });

  const handlePlayNowClick = () => {
    trackEvent({
      trackingData: {},
      trackingName: END_OF_VIDEO_LINK_TRACKING_NAME,
      action: 'click',
    });
    if (nextVideoInfo) {
      router.push({
        pathname: nextVideoInfo.pathname,
        query: { ...nextVideoInfo.query, playNow: 'true' },
      });
    }
  };

  return (
    <div css={styles.videoEndOverlay}>
      {nextVideoInfo ? (
        <>
          <Typography variant="h2semibold" color="invertBody" css={styles.header}>
            {isAutoPlay ? _t('Up next in #{countdownTime}...', { countdownTime }) : _t('Up next')}
          </Typography>
          <Grid container spacing={16}>
            <Grid item xs css={styles.videoThumbnailMobile}>
              <VideoThumbnailLink
                itemId={nextVideoInfo.id}
                name={nextVideoInfo.name}
                courseId={courseId}
                to={{ pathname: nextVideoInfo.pathname, query: { ...nextVideoInfo.query, playNow: 'true' } }}
              />
            </Grid>
            <Grid item xs={8} sm={12}>
              {partner && <PartnerInfo {...partner} />}
              <Typography2 component="h3" variant="subtitleMedium" color="invertBody" css={styles.nextVideoName}>
                {nextVideoInfo.name}
              </Typography2>
              <Typography2 component="p" variant="bodyPrimary" color="invertBody" css={styles.videoDuration}>
                {getDurationFormatted(nextVideoInfo.timeCommitment)}
              </Typography2>
              <div css={styles.videoThumbnail}>
                <VideoThumbnailLink
                  itemId={nextVideoInfo.id}
                  name={nextVideoInfo.name}
                  courseId={courseId}
                  to={{ pathname: nextVideoInfo.pathname, query: { ...nextVideoInfo.query, playNow: 'true' } }}
                />
              </div>
              <TrackedDiv trackingName={END_OF_VIDEO_LINK_TRACKING_NAME} withVisibilityTracking>
                <Button variant="ghostInvert" onClick={handlePlayNowClick} css={styles.playNowButton}>
                  {_t('Play Now')}
                </Button>
              </TrackedDiv>
            </Grid>
          </Grid>
        </>
      ) : (
        <>
          <Typography variant="h2semibold" color="invertBody" css={styles.header}>
            {_t('Recommended for you')}
          </Typography>
          {partner && <PartnerInfo {...partner} />}
          <Typography2 component="h3" variant="subtitleMedium" color="invertBody" css={styles.nextVideoName}>
            {courseName}
          </Typography2>
          {skills && (
            <Typography2 component="h3" variant="subtitleMedium" color="invertBody" css={styles.skills}>
              {_t('Skills you’ll gain: #{skills}', { skills: skills?.join(', ') })}
            </Typography2>
          )}
          {ratings && <CourseRatings {...ratings} />}
          <Typography2 component="p" variant="bodyPrimary" color="invertBody" css={styles.courseMeta}>
            {[_t('Course'), totalCourseDuration].filter(Boolean).join(' • ')}
          </Typography2>
          <Button
            variant="primaryInvert"
            href={programHomeMiniXdpLink}
            component={TrackedA}
            withVisibilityTracking
            trackingName="sfc_end_of_video_course_link"
            css={styles.seeDetailsButton}
          >
            {_t('See Details')}
          </Button>
        </>
      )}
    </div>
  );
};

export default VideoEndOverlay;
